import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { usePlayerOptions } from "../hooks/usePlayerOptions";
import { useResizeObserver } from "../hooks/useResizeObserver";
import styles from "./focalPoint/FocalPoint.css";
import { calculatePercentNotVisible } from "./focalPoint/calculatePercentNotVisible";
import { calculateStyle } from "./focalPoint/calculateStyle";
import { useVisibleTile } from "../context/VisibleTileContext";

function getEffectiveFitMode(
  componentFitMode: "contain" | "cover",
  playerOptionFitMode: "fit" | "fill" | null,
): "cover" | "contain" {
  if (playerOptionFitMode === "fill") {
    return "cover";
  }

  if (playerOptionFitMode === "fit") {
    return "contain";
  }

  return componentFitMode;
}

//TODO: On load there are two renders.
// 1. Write test to detect excess renders
// 2. Fix it
//
// Don't bother fixing it if we can't test that it's fixed.

interface Props
  extends React.PropsWithChildren,
    Pick<
      React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >,
      "className"
    > {
  active?: boolean | undefined;
  containThreshold?: number | undefined;
  fitMode: "contain" | "cover";
  focalPoint: FocalPoint;
  ignorePlayerOptionFitMode?: boolean | undefined;
  manualContainerHeight?: number | undefined;
  manualContainerWidth?: number | undefined;
  onShouldContainChange?: ((value: boolean | null) => void) | undefined;
  videoId?: number | undefined;
}

const FocalPoint = ({
  active,
  children,
  className = "",
  /**
   * This value represents a percentage of content that can be cropped out
   * before the player will force content to be contained. In other words, a
   * maximum of this percentage can be hidden; if more than that is hidden, then
   * we don't crop _any_ content.
   */
  containThreshold = 0.125, // Magic number per @mslanza
  fitMode,
  focalPoint,
  ignorePlayerOptionFitMode = false,
  manualContainerHeight,
  manualContainerWidth,
  onShouldContainChange,
  videoId,
  ...passthroughProps
}: Props) => {
  const { tileConfig, visibleTile } = useVisibleTile({
    isActive: active,
    videoId,
  });

  const playerOptions = usePlayerOptions();
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const containerDimensions = useResizeObserver(containerRef);
  const previousEffectiveShouldContain = React.useRef<boolean | null>(null);

  const fitModeUserOption =
    (!ignorePlayerOptionFitMode && playerOptions.fitMode) || null;

  const effectiveFitMode = getEffectiveFitMode(fitMode, fitModeUserOption);

  let style: React.CSSProperties = {
    width: 0,
    height: 0,
  };

  // We have to check for a function because a ref can be either a
  // `React.RefObject` or a `function`. String refs are deprecated so don't even
  // bother.
  let percentNotVisible = null;

  const effectiveThreshold =
    fitModeUserOption === "fill" && !ignorePlayerOptionFitMode
      ? Infinity
      : containThreshold;

  if (containerDimensions.width != null && containerDimensions.height != null) {
    percentNotVisible = calculatePercentNotVisible({
      tiles: tileConfig,
      focalPoint,
      containerWidth:
        manualContainerWidth == null
          ? containerDimensions.width
          : manualContainerWidth,
      containerHeight:
        manualContainerHeight == null
          ? containerDimensions.height
          : manualContainerHeight,
    });
  }

  /**
   * Represents the effective value background size. If we can't yet determine
   * the value because we don't yet have the layout sizes, this will be null.
   */
  const effectiveShouldContain =
    percentNotVisible == null
      ? null
      : effectiveFitMode === "contain" ||
        (effectiveFitMode === "cover" && percentNotVisible) >
          effectiveThreshold;

  if (previousEffectiveShouldContain.current !== effectiveShouldContain) {
    previousEffectiveShouldContain.current = effectiveShouldContain;

    onShouldContainChange?.(effectiveShouldContain);
  }

  if (containerDimensions.width != null && containerDimensions.height != null) {
    style = {
      ...style,
      ...calculateStyle({
        tiles: tileConfig,
        contain: effectiveShouldContain ?? false,
        focalPoint,
        containerHeight: containerDimensions.height,
        containerWidth: containerDimensions.width,
      }),
    };
  }

  return (
    <div
      {...passthroughProps}
      className={classnames(className, styles.root)}
      data-fit-mode={fitMode}
      data-testid="FocalPoint"
      data-percent-not-visible={
        percentNotVisible ? Math.round(100 * percentNotVisible) / 100 : "null"
      }
      ref={setContainerRef}
    >
      <div className={styles.child} style={style}>
        {tileConfig.rows * tileConfig.columns > 1 ? (
          <div
            className={styles.stackedChild}
            style={{
              width: `${100 * tileConfig.columns}%`,
              height: `${100 * tileConfig.rows}%`,
              top: `${-visibleTile.row * 100}%`,
              left: `${-visibleTile.column * 100}%`,
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

FocalPoint.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  containThreshold: PropTypes.number,
  fitMode: PropTypes.oneOf(["contain", "cover"]).isRequired,
  focalPoint: PropTypes.shape({
    aspectRatio: PropTypes.number,
    focalX: PropTypes.number.isRequired,
    focalY: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
  ignorePlayerOptionFitMode: PropTypes.bool,
  manualContainerHeight: PropTypes.number,
  manualContainerWidth: PropTypes.number,
  onShouldContainChange: PropTypes.func,
  videoId: PropTypes.number,
};

export default FocalPoint;
